import { computed } from '@angular/core';

import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

import { FEATURE_FLAGS } from './feature-flags.type';

export interface FeatureFlagsState {
  allowed: FEATURE_FLAGS[];
  restrictions: FEATURE_FLAGS[];
}

const initialState: FeatureFlagsState = {
  allowed: [],
  restrictions: [],
};

/**
 * Feature flags store
 * - allowed: whitelist; if empty, everything is allowed unless restricted
 * - restrictions: blacklist; always denies if present
 *
 * Usage examples:
 *  const flags = inject(FeatureFlagsStore);
 *  // in component class
 *  readonly ecommerceAllowed = flags.isAllowed(FEATURE_FLAGS.ECOMMERCE);
 *  // in template
 *  @if (ecommerceAllowed()) { ... }
 */
export const FeatureFlagsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(store => ({
    setAllowed(allowed: string[]): void {
      patchState(store, { allowed: allowed as FEATURE_FLAGS[] });
    },
    setRestrictions(restrictions: string[]): void {
      patchState(store, { restrictions: restrictions as FEATURE_FLAGS[] });
    },
    setFlags(flags: Partial<FeatureFlagsState>): void {
      patchState(store, flags);
    },
    clear(): void {
      patchState(store, initialState);
    },
    // Only allow checking for described flags
    isAllowed: (name: FEATURE_FLAGS) => computed(() => {
      if (store.restrictions().includes(name)) return false;

      const allowed = store.allowed();

      if (allowed.length === 0) return true;

      return allowed.includes(name);
    }),
  })),
);
